import { useCallback, useMemo, useState } from 'react'
import type { RateType } from '~/atoms'
import { Button, ToggleGroup } from '~/components/ui'
import { cn } from '~/utils'
import MapMinMaxFilter from './MinMax'

function getSuffix(rateType: RateType) {
  switch (rateType) {
    case 'annual':
      return 'SF/yr'
    case 'monthly':
      return 'SF/mo'
    case 'totalMonthly':
      return '/mo'
    default:
      return 'SF/yr'
  }
}

export default function MapRateFilter({
  className,
  filterValues,
  rateType = 'annual',
  updateFilter,
}: {
  className?: string
  filterValues: (number | undefined)[]
  rateType?: RateType
  updateFilter: (updates: Record<string, any>) => void
}) {
  const [resetKey, setResetKey] = useState(Date.now())

  const suffix = useMemo(() => {
    return getSuffix(rateType)
  }, [rateType])

  const handleRateTypeChange = useCallback(
    (newRateType: RateType) => {
      if (newRateType === rateType) return

      updateFilter({
        rateType: newRateType,
      })
    },
    [rateType, updateFilter]
  )

  const handleRateUpdate = useCallback(
    (updates: Record<string, any>) => {
      updateFilter(updates)
    },
    [updateFilter]
  )

  const handleReset = useCallback(() => {
    updateFilter({ rates: undefined, rateType: 'annual' })
    setResetKey(Date.now())
  }, [updateFilter])

  const hasValues = filterValues.filter((v) => v).length > 0

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between">
        <p className="text-sm">
          Lease Rate
          {hasValues && (
            <Button
              variant="link"
              size="none"
              className="ml-2"
              onClick={handleReset}>
              (reset)
            </Button>
          )}
        </p>
        <ToggleGroup
          type="single"
          value={rateType}
          onValueChange={(value) => {
            if (value) handleRateTypeChange(value as RateType)
          }}
          className="h-7">
          <ToggleGroup.Item
            value="annual"
            className="h-6 px-1.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
            $/SF/yr
          </ToggleGroup.Item>
          <ToggleGroup.Item
            value="monthly"
            className="h-6 px-1.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
            $/SF/mo
          </ToggleGroup.Item>
          <ToggleGroup.Item
            value="totalMonthly"
            className="h-6 px-1.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
            Total/mo
          </ToggleGroup.Item>
        </ToggleGroup>
      </div>
      <MapMinMaxFilter
        key={resetKey}
        name="rates"
        label=""
        start="$"
        end={suffix}
        filterValues={filterValues}
        updateFilter={handleRateUpdate}
        hideReset
      />
    </div>
  )
}
